import camelcaseKeys from 'camelcase-keys'
import 'moment/locale/ja'
import config from '.'
import { loadDemoMocks } from '../store/Demo/operations'
import { getToken } from '../utils/tokenUtil'

type ApiResponse<T = unknown> = {
	data: T
	status: number
}

type RequestConfig = {
	params?: Record<string, unknown>
}

type ApiClient = {
	get: <T = unknown>(
		url: string,
		config?: RequestConfig,
	) => Promise<ApiResponse<T>>
	post: <T = unknown>(url: string, data?: unknown) => Promise<ApiResponse<T>>
	put: <T = unknown>(url: string, data?: unknown) => Promise<ApiResponse<T>>
	delete: <T = unknown>(url: string, data?: unknown) => Promise<ApiResponse<T>>
}

const isMockPage = () => !!window.location.href.match('/houses/0')

function buildUrl(
	base: string,
	path: string,
	params?: Record<string, unknown>,
): string {
	const url = new URL(path, base)
	if (params) {
		Object.entries(params).forEach(([key, value]) => {
			if (value !== undefined && value !== null) {
				url.searchParams.set(key, String(value))
			}
		})
	}
	return url.toString()
}

async function request<T>(
	baseURL: string,
	method: string,
	url: string,
	headers: Record<string, string>,
	data?: unknown,
	params?: Record<string, unknown>,
	mockHandler?: MockHandler,
): Promise<ApiResponse<T>> {
	if (mockHandler) {
		const mockResult = mockHandler(method, url, data)
		if (mockResult) return mockResult as ApiResponse<T>
	}

	const fullUrl = buildUrl(baseURL, url, params)
	const init: RequestInit = {
		method,
		headers: { ...headers, 'Content-Type': 'application/json' },
	}
	if (data !== undefined && method !== 'GET') {
		init.body = JSON.stringify(data)
	}

	const res = await fetch(fullUrl, init)
	const status = res.status
	let body: unknown = null
	const text = await res.text()
	if (text) {
		try {
			body = JSON.parse(text)
		} catch {
			body = text
		}
	}

	if (!res.ok) {
		const error: Error & { response?: { data: unknown } } = new Error(
			`Request failed with status ${status}`,
		)
		error.response = { data: body }
		throw error
	}

	const camelized = body
		? camelcaseKeys(body as Record<string, unknown>, { deep: true })
		: body

	return { data: camelized as T, status }
}

type MockResult = { data: unknown; status: number }
type MockHandler = (
	method: string,
	url: string,
	data?: unknown,
) => MockResult | null

let mockHandler: MockHandler | null = null

export function setMockHandler(handler: MockHandler) {
	mockHandler = handler
}

export const cli = (): ApiClient => {
	const baseURL = config.api.url
	const headers: Record<string, string> = {}

	const token = getToken()
	if (token) headers['Authorization'] = token
	console.log(token)

	if (isMockPage()) {
		loadDemoMocks()
	}

	return {
		get: <T = unknown>(url: string, cfg?: RequestConfig) =>
			request<T>(
				baseURL,
				'GET',
				url,
				headers,
				undefined,
				cfg?.params,
				mockHandler ?? undefined,
			),
		post: <T = unknown>(url: string, data?: unknown) =>
			request<T>(
				baseURL,
				'POST',
				url,
				headers,
				data,
				undefined,
				mockHandler ?? undefined,
			),
		put: <T = unknown>(url: string, data?: unknown) =>
			request<T>(
				baseURL,
				'PUT',
				url,
				headers,
				data,
				undefined,
				mockHandler ?? undefined,
			),
		delete: <T = unknown>(url: string, data?: unknown) =>
			request<T>(
				baseURL,
				'DELETE',
				url,
				headers,
				data,
				undefined,
				mockHandler ?? undefined,
			),
	}
}
